// The launcher: every app, full screen, from the Apps tab. Search and Settings
// at the top, Prime under them, then the apps in your order, then pinned pages
// and lists. Hold an app for its quick actions; Arrange (or the menu's
// "Arrange apps") makes them wiggle: drag to reorder, the eye hides one, and
// hidden apps wait at the bottom to be brought back.
import { AgentsNow } from '@/components/AgentsNow'
import { SuiteStrip } from '@/components/SuiteSwitcher'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { Eye, EyeOff, Plus, Search as SearchIcon, Settings as SettingsIcon, X } from 'lucide-react'
import { cx } from '@/components/ui'
import { isPinnedNav } from '@/lib/useNav'
import { navActive } from '@/lib/nav'
import { liveLead } from '@/lib/lead'
import { useAllRuns, useResolvedLook } from '@/db/hooks'
import type { NavItem } from '@/lib/nav'
import { hideApp, launcherColumns, launcherOrder, moveInLauncher, showApp, type PhoneLayout } from './layout'
import { savePhone, useEditing, useLauncher, usePhoneApps, usePhoneLayout } from './phone'
import { AppLabel, AppTile, useHold } from './AppIcon'
import { AppMenu } from './AppMenu'
import { AskPrimeBar, AskPrimeSheet } from './AskPrime'
import { Backdrop } from './Backdrop'
import { APPEARANCE_ICON, AppearanceSheet } from '@/components/AppearancePicker'
import { useUi } from '@/state/ui'
import { appearanceOf } from '@/lib/themeLayers'

export const Launcher = () => {
  const open = useLauncher((s) => s.open)
  const setOpen = useLauncher((s) => s.setOpen)
  if (!open) return null
  return <LauncherInner onClose={() => setOpen(false)} />
}

const LauncherInner = ({ onClose }: { onClose: () => void }) => {
  const saved = usePhoneLayout()
  const { apps } = usePhoneApps()
  const navigate = useNavigate()
  const path = useRouterState({ select: (s) => s.location.pathname })
  // Prime's thread lights Prime, not Agents
  const lead = liveLead(useAllRuns())?.id
  const editing = useEditing((s) => s.launcher)
  const setEditing = useEditing((s) => s.set)
  const [draft, setDraft] = useState<PhoneLayout | null>(null)
  const layout = editing && draft ? draft : saved
  const [query, setQuery] = useState('')
  const [menuApp, setMenuApp] = useState<NavItem | null>(null)
  const [asking, setAsking] = useState(false)
  const [appearanceOpen, setAppearanceOpen] = useState(false)
  const AppearanceIcon = APPEARANCE_ICON[appearanceOf(useResolvedLook())]
  const [drag, setDrag] = useState<{
    id: string
    pointer: number
    x: number
    y: number
    moving: boolean
  } | null>(null)
  if (editing && !draft) setDraft(saved)
  // Escape closes, like every sheet; a sheet opened from here (a held app, Ask Prime) takes it first
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || menuApp || asking) return
      onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, menuApp, asking])

  const main = apps.filter((a) => !isPinnedNav(a.key) && a.key !== 'search' && a.key !== 'settings')
  const pinned = apps.filter((a) => isPinnedNav(a.key) && !layout.hidden.includes(a.key))
  const ids = main.map((a) => a.key)
  const byId = new Map(apps.map((a) => [a.key, a]))
  const ordered = launcherOrder(layout, ids)
    .map((id) => byId.get(id))
    .filter((a): a is NavItem => !!a)
  const hidden = layout.hidden.map((id) => byId.get(id)).filter((a): a is NavItem => !!a)

  const close = () => {
    if (editing) finish()
    onClose()
  }
  const finish = () => {
    setDraft(null)
    setEditing({ launcher: false })
  }
  const commit = (next: PhoneLayout) => {
    setDraft(next)
    void savePhone(next)
  }

  const onDown = (e: React.PointerEvent, id: string) => {
    if (!editing) return
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    setDrag({ id, pointer: e.pointerId, x: e.clientX, y: e.clientY, moving: false })
  }
  const onMove = (e: React.PointerEvent) => {
    if (!drag || drag.pointer !== e.pointerId || !draft) return
    const moving = drag.moving || Math.hypot(e.clientX - drag.x, e.clientY - drag.y) > 6
    if (moving !== drag.moving) setDrag({ ...drag, moving })
    if (!moving) return
    const over = document
      .elementsFromPoint(e.clientX, e.clientY)
      .map((n) => (n as HTMLElement).closest<HTMLElement>('[data-launcher-id]'))
      .find((n) => n && n.dataset.launcherId !== drag.id)
    if (!over) return
    const visible = launcherOrder(draft, ids)
    const from = visible.indexOf(drag.id)
    const to = visible.indexOf(over.dataset.launcherId!)
    if (from >= 0 && to >= 0 && from !== to) setDraft(moveInLauncher(draft, ids, from, to))
  }
  const onUp = (e: React.PointerEvent) => {
    if (!drag || drag.pointer !== e.pointerId) return
    if (drag.moving && draft) void savePhone(draft)
    setDrag(null)
  }

  const cols = { gridTemplateColumns: `repeat(${launcherColumns(layout.iconSize)}, minmax(0, 1fr))` }
  const q = query.trim().toLowerCase()
  const matches = q ? apps.filter((a) => a.label.toLowerCase().includes(q)) : []

  return (
    <div
      // under the tab bar (z-30), so the Apps tab that opened it closes it again
      className="fixed inset-0 z-[25] flex flex-col bg-ink sheet-in"
      role="dialog"
      aria-modal="true"
      aria-label="Apps"
      style={{ top: 'var(--vv-top)', height: 'var(--vv-height)' }}
    >
      {/* the Home backdrop, quieter behind the grid so names stay easy to read */}
      <Backdrop kind={saved.wallpaper} />
      <div className="absolute inset-0 bg-ink/60 pointer-events-none" aria-hidden />
      <div
        className="relative flex-1 min-h-0 overflow-y-auto px-4 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-[calc(6rem+env(safe-area-inset-bottom))] hold-safe"
        data-testid="more-sheet"
      >
        <div className="flex items-center gap-2">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const first = matches[0]
              close()
              setQuery('')
              // an app's name opens the app; anything else searches your things
              if (first && first.label.toLowerCase() === q) void navigate({ to: first.to })
              else void navigate({ to: '/search', search: q ? { q: query.trim() } : {} })
            }}
            className="flex-1 min-w-0 flex items-center gap-2 rounded-2xl bg-raised/80 border border-line px-3"
          >
            <SearchIcon size={16} className="text-muted shrink-0" aria-hidden />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              // short enough to read whole beside the row of buttons on any phone; apps first, then your things
              placeholder="Search"
              aria-label="Search apps, tasks and pages"
              className="bare-input flex-1 min-w-0 px-0 py-2.5"
              enterKeyHint="search"
              data-testid="more-search"
            />
          </form>
          <Link
            to="/settings"
            onClick={close}
            aria-label="Settings"
            title="Settings"
            className={cx(
              'h-11 w-11 shrink-0 rounded-2xl border flex items-center justify-center transition-colors',
              path.startsWith('/settings')
                ? 'bg-accent text-on-accent border-transparent'
                : 'bg-raised/80 border-line text-fg-2 hover:bg-line',
            )}
            data-testid="more-settings"
          >
            <SettingsIcon size={19} aria-hidden />
          </Link>
          <button
            type="button"
            onClick={() => {
              close()
              useUi.getState().setQuickAdd(true)
            }}
            aria-label="Quick add"
            title="Quick add"
            className="h-11 w-11 shrink-0 rounded-2xl bg-raised/80 border border-line text-fg-2 flex items-center justify-center"
            data-testid="launcher-add"
          >
            <Plus size={19} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setAppearanceOpen(true)}
            aria-label="Appearance"
            title="Appearance"
            className="h-11 w-11 shrink-0 rounded-2xl bg-raised/80 border border-line text-fg-2 flex items-center justify-center"
            data-testid="launcher-appearance"
          >
            <AppearanceIcon size={18} aria-hidden />
          </button>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="h-11 w-11 shrink-0 rounded-2xl bg-raised/80 border border-line text-fg-2 flex items-center justify-center"
            data-testid="launcher-close"
          >
            <X size={18} aria-hidden />
          </button>
        </div>

        {!q && <SuiteStrip />}
        {q ? (
          <div className="mt-5">
            {matches.length ? (
              <div className="grid gap-x-1.5 gap-y-4" style={cols}>
                {matches.map((a) => (
                  <LauncherApp
                    key={a.key}
                    app={a}
                    tint={layout.icons}
                    size={layout.iconSize}
                    active={navActive(a.to, path, a.exact, lead)}
                    onOpen={() => {
                      close()
                      setQuery('')
                      void navigate({ to: a.to })
                    }}
                    onHold={() => setMenuApp(a)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-faint px-1">No app by that name. Enter searches your things.</p>
            )}
          </div>
        ) : (
          <>
            <AskPrimeBar className="mt-4" onOpen={() => setAsking(true)} />
            <div className="mt-3 rounded-2xl border border-line bg-raised/80 p-1 empty:hidden">
              <AgentsNow label={false} onPick={close} />
            </div>
            <div className="flex items-center justify-between mt-5 mb-2 px-1">
              <h2 className="text-[13px] font-semibold text-muted">Apps</h2>
              <button
                type="button"
                onClick={() => (editing ? finish() : setEditing({ launcher: true }))}
                className={cx(
                  'text-xs min-h-8 px-2 rounded-full',
                  editing ? 'bg-accent text-on-accent px-3 font-medium' : 'text-faint hover:text-fg',
                )}
                data-testid="launcher-arrange"
              >
                {editing ? 'Done' : 'Arrange'}
              </button>
            </div>
            <div className="grid gap-x-1.5 gap-y-4" style={cols}>
              {ordered.map((a, i) => (
                <div
                  key={a.key}
                  data-launcher-id={a.key}
                  className={cx(
                    'relative',
                    editing && 'os-jiggle touch-none',
                    editing && i % 2 === 1 && 'os-jiggle-alt',
                    drag?.moving && drag.id === a.key && 'opacity-40 scale-110',
                  )}
                  onPointerDown={(e) => onDown(e, a.key)}
                  onPointerMove={onMove}
                  onPointerUp={onUp}
                  onPointerCancel={onUp}
                >
                  <LauncherApp
                    app={a}
                    tint={layout.icons}
                    size={layout.iconSize}
                    active={navActive(a.to, path, a.exact, lead)}
                    editing={editing}
                    onOpen={() => {
                      close()
                      void navigate({ to: a.to })
                    }}
                    onHold={() => setMenuApp(a)}
                  />
                  {editing && a.key !== 'home' && (
                    <button
                      type="button"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => commit(hideApp(layout, a.key))}
                      aria-label={`Hide ${a.label}`}
                      className="absolute -top-1 left-0.5 h-6 w-6 rounded-full bg-raised border border-line-strong text-muted flex items-center justify-center"
                      data-testid="launcher-hide"
                    >
                      <EyeOff size={12} aria-hidden />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {pinned.length > 0 && (
              <>
                <h2 className="mt-6 mb-2 px-1 text-[13px] font-semibold text-muted">Pinned</h2>
                <div className="grid gap-x-1.5 gap-y-4" style={cols}>
                  {pinned.map((a) => (
                    <LauncherApp
                      key={a.key}
                      app={a}
                      tint={layout.icons}
                      size={layout.iconSize}
                      active={navActive(a.to, path, a.exact, lead)}
                      onOpen={() => {
                        close()
                        void navigate({ to: a.to })
                      }}
                      onHold={() => setMenuApp(a)}
                    />
                  ))}
                </div>
              </>
            )}
            {editing && hidden.length > 0 && (
              <>
                <h2 className="mt-6 mb-2 px-1 text-[13px] font-semibold text-muted">Hidden</h2>
                <div className="grid gap-x-1.5 gap-y-4" style={cols}>
                  {hidden.map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => commit(showApp(layout, a.key))}
                      className="flex flex-col items-center gap-1.5 min-w-0 opacity-60"
                      aria-label={`Show ${a.label}`}
                      data-testid={`launcher-show-${a.key}`}
                    >
                      <span className="relative">
                        <AppTile app={a} tint={layout.icons} size={layout.iconSize} />
                        <span className="absolute -top-1 -left-1 h-6 w-6 rounded-full bg-accent text-on-accent flex items-center justify-center">
                          <Eye size={12} aria-hidden />
                        </span>
                      </span>
                      <AppLabel>{a.label}</AppLabel>
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
      <AppMenu app={menuApp} layout={layout} from="launcher" onClose={() => setMenuApp(null)} />
      <AppearanceSheet open={appearanceOpen} onClose={() => setAppearanceOpen(false)} />
      <AskPrimeSheet
        open={asking}
        onClose={() => {
          setAsking(false)
        }}
      />
    </div>
  )
}

const LauncherApp = ({
  app,
  tint,
  active,
  editing = false,
  size,
  onOpen,
  onHold,
}: {
  app: NavItem
  tint: 'colour' | 'mono'
  active: boolean
  size: number
  editing?: boolean
  onOpen: () => void
  onHold: () => void
}) => {
  const hold = useHold(onHold)
  return (
    <Link
      to={app.to}
      onClick={(e) => {
        e.preventDefault()
        if (editing || hold.held()) return
        onOpen()
      }}
      {...(editing ? {} : hold.handlers)}
      className="flex flex-col items-center gap-1.5 min-w-0 text-center"
      draggable={false}
    >
      <AppTile app={app} tint={tint} active={active} size={size} />
      <AppLabel active={active}>{app.label}</AppLabel>
    </Link>
  )
}
